import Text from '@cartwhl/ui/base/TextNew';

import { firstContentSpacing } from '../first-content-spacing';
import { pageFrame } from '../page-frame';
import { publicPageGutters } from '../public-page-gutters';
import {
  type CompanyInvestor,
  type CompanyTeamMember,
  companyPageContent,
} from './content';

interface TeamCardProps {
  member: CompanyTeamMember;
}

function TeamCard({ member }: TeamCardProps) {
  return (
    <article className="bg-secondary flex min-h-[188px] flex-col gap-[42px] rounded-2xl px-8 py-10 min-[720px]:min-h-[300px] lg:min-h-[206px] 2xl:min-h-[193px]">
      <Text as="h3" token={{ base: '24', lg: '32' }} leading="body">
        {member.name}
      </Text>
      <Text
        as="p"
        token={{ base: '16', '2xl': '24' }}
        leading="body"
        className="text-secondary"
      >
        {member.role}
      </Text>
    </article>
  );
}

interface InvestorCardProps {
  investor: CompanyInvestor;
}

function InvestorCard({ investor }: InvestorCardProps) {
  return (
    <div className="bg-secondary flex h-[188px] items-center justify-center overflow-hidden rounded-2xl px-8 py-10 min-[720px]:h-[206px]">
      <img
        src={investor.media.src}
        alt={`${investor.name} logo`}
        className={`max-h-full max-w-full object-contain ${investor.id === 'wndr' ? 'h-[47px] w-[184px] mix-blend-multiply' : ''}`}
      />
    </div>
  );
}

export default function CompanyPage() {
  const { intro, investors, manifesto, team } = companyPageContent;

  return (
    <div className={pageFrame}>
      <section
        className={`border-primary border-b pb-10 ${firstContentSpacing} sm:pb-20 ${publicPageGutters}`}
      >
        <div className="flex flex-col gap-10">
          <Text
            as="p"
            token={{ base: '24/medium', lg: '32/medium' }}
            leading="body"
          >
            {intro.label}
          </Text>
          <div className="flex flex-col gap-10 xl:grid xl:grid-cols-2 xl:gap-10">
            <Text
              as="h1"
              token={{ base: '24', lg: '32' }}
              leading="body"
              className="text-secondary text-pretty"
            >
              <span className="text-primary cw-green-underline">
                {intro.emphasized}
              </span>
              {intro.remainder}
            </Text>
            <Text
              as="p"
              token={{ base: '24', lg: '32' }}
              leading="body"
              className="text-secondary text-pretty"
            >
              {intro.supporting}
            </Text>
          </div>
        </div>
      </section>

      <section className={`border-primary border-b py-10 sm:py-20 ${publicPageGutters}`}>
        <div className="mx-auto flex max-w-[816px] flex-col gap-10">
          {manifesto.sections.map((section, index) => (
            <div key={section.id} className="flex flex-col gap-10">
              <Text
                as={index === 0 ? 'h2' : 'h3'}
                token={{ base: '24/medium', lg: '32/medium' }}
                leading="editorial"
              >
                {section.title}
              </Text>
              <Text
                as="div"
                token="24"
                leading="editorial"
                className="cw-text-box-none text-secondary flex flex-col gap-8"
              >
                {section.description.split('\n\n').map((paragraph) => (
                  <p key={paragraph}>{paragraph}</p>
                ))}
              </Text>
            </div>
          ))}
        </div>
      </section>

      <section className={`border-primary border-b py-10 sm:py-20 ${publicPageGutters}`}>
        <div className="flex flex-col gap-20">
          <Text
            as="h2"
            token={{ base: '24/medium', lg: '32/medium' }}
            leading="body"
          >
            Team
          </Text>
          <div className="grid grid-cols-1 gap-6 min-[720px]:grid-cols-2 min-[720px]:gap-10 lg:grid-cols-3 lg:gap-6 2xl:grid-cols-4">
            {team.map((member) => (
              <TeamCard key={member.id} member={member} />
            ))}
          </div>
        </div>
      </section>

      <section className={`border-primary border-b py-10 sm:py-20 ${publicPageGutters}`}>
        <div className="flex flex-col gap-20">
          <Text
            as="h2"
            token={{ base: '24/medium', lg: '32/medium' }}
            leading="body"
          >
            Investors
          </Text>
          <div className="grid grid-cols-1 gap-6 min-[720px]:grid-cols-2 min-[720px]:gap-10 lg:grid-cols-3 lg:gap-6 2xl:grid-cols-4">
            {investors.map((investor) => (
              <InvestorCard key={investor.id} investor={investor} />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
